import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  fetchAnalyticsOverview,
  fetchMentorUsage,
  fetchMentorAnalytics,
  fetchAnalyticsFilterOptions,
} from '../../api/analyticsApi';
import AnalyticsFilters from '../analytics/AnalyticsFilters';
import OverviewTrends from '../analytics/OverviewTrends';
import MentorUsageTable from '../analytics/MentorUsageTable';
import MentorDrilldown from '../analytics/MentorDrilldown';
import '../../styles/analytics.css';

const DEFAULT_FILTERS = {
  preset: '30d',
  from: null,
  to: null,
  platform: null,
  appVersion: null,
  eventName: null,
  screen: null,
  hierarchy: null,
  supervisorId: null,
  batchId: null,
  search: null,
  activity: null,
  minEvents: null,
  minSessions: null,
};

/** Filters the API accepts on every endpoint. */
const sharedFilterKeys = [
  'preset', 'from', 'to', 'platform', 'appVersion', 'eventName',
  'screen', 'hierarchy', 'supervisorId', 'batchId', 'search',
];

const pick = (obj, keys) => keys.reduce((acc, k) => {
  if (obj[k] !== null && obj[k] !== undefined && obj[k] !== '') acc[k] = obj[k];
  return acc;
}, {});

const AnalyticsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedMentorId = searchParams.get('mentor');

  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [options, setOptions] = useState({});
  const [overview, setOverview] = useState(null);
  const [mentorList, setMentorList] = useState(null);
  const [mentorDetail, setMentorDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState('events');
  const [order, setOrder] = useState('desc');

  // Serialised so the effects below re-run on any filter change without
  // depending on object identity.
  const filterKey = useMemo(() => JSON.stringify(filters), [filters]);

  useEffect(() => {
    let cancelled = false;
    fetchAnalyticsFilterOptions()
      .then((data) => { if (!cancelled) setOptions(data); })
      .catch(() => { /* pickers degrade to the values already in `filters` */ });
    return () => { cancelled = true; };
  }, []);

  // Overview + mentor table. Skipped while a drill-down is open, since that
  // view replaces both.
  useEffect(() => {
    if (selectedMentorId) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);

    const shared = pick(filters, sharedFilterKeys);
    const listParams = {
      ...shared,
      ...pick(filters, ['activity', 'minEvents', 'minSessions']),
      sort,
      order,
      page,
      limit: 25,
    };

    Promise.all([fetchAnalyticsOverview(shared), fetchMentorUsage(listParams)])
      .then(([ov, list]) => {
        if (cancelled) return;
        setOverview(ov);
        setMentorList(list);
      })
      .catch((err) => {
        if (cancelled) return;
        const message = err.response?.data?.message || err.message || 'Failed to load analytics';
        setError(message);
        toast.error(message);
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [filterKey, page, sort, order, selectedMentorId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selectedMentorId) {
      setMentorDetail(null);
      return undefined;
    }
    let cancelled = false;
    setDetailLoading(true);
    setError(null);

    fetchMentorAnalytics(selectedMentorId, pick(filters, sharedFilterKeys))
      .then((data) => { if (!cancelled) setMentorDetail(data); })
      .catch((err) => {
        if (cancelled) return;
        const message = err.response?.data?.message || err.message || 'Failed to load mentor analytics';
        setError(message);
        toast.error(message);
      })
      .finally(() => { if (!cancelled) setDetailLoading(false); });

    return () => { cancelled = true; };
  }, [selectedMentorId, filterKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFilterChange = useCallback((next) => {
    setFilters(next);
    setPage(1);
  }, []);

  const handleReset = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
    setPage(1);
  }, []);

  const handleSelectMentor = useCallback((mentorId) => {
    // Held in the URL so a drill-down is linkable and the back button works.
    setSearchParams({ mentor: String(mentorId) });
  }, [setSearchParams]);

  const handleBack = useCallback(() => {
    setSearchParams({});
  }, [setSearchParams]);

  const handleSortChange = useCallback((nextSort, nextOrder) => {
    setSort(nextSort);
    setOrder(nextOrder);
    setPage(1);
  }, []);

  // Feed the resolved range back so switching to "Custom" pre-fills real dates.
  const filtersWithResolved = useMemo(() => ({
    ...filters,
    resolvedFrom: overview?.range?.from || mentorDetail?.range?.from,
    resolvedTo: overview?.range?.to || mentorDetail?.range?.to,
  }), [filters, overview, mentorDetail]);

  return (
    <div className="analytics-page">
      <div className="analytics-page-header">
        <div>
          <h1 className="analytics-page-title">MentorHub Analytics</h1>
          <p className="analytics-page-subtitle">
            Mentor app usage from the MentorHub mobile app.
            {overview?.range && (
              <span className="analytics-range-label">
                {' '}{overview.range.from} → {overview.range.to} ({overview.range.days} days)
              </span>
            )}
          </p>
        </div>
      </div>

      <AnalyticsFilters
        filters={filtersWithResolved}
        options={options}
        onChange={handleFilterChange}
        onReset={handleReset}
        disabled={loading && !overview}
      />

      {error && !loading && (
        <div className="analytics-error">
          <i className="bi bi-exclamation-octagon" aria-hidden="true" /> {error}
        </div>
      )}

      {selectedMentorId ? (
        <MentorDrilldown
          data={mentorDetail}
          loading={detailLoading}
          onBack={handleBack}
        />
      ) : (
        <>
          {loading && !overview ? (
            <div className="analytics-loading">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading analytics</span>
              </div>
            </div>
          ) : (
            <>
              <OverviewTrends data={overview} />
              <MentorUsageTable
                data={mentorList}
                loading={loading}
                sort={sort}
                order={order}
                onSortChange={handleSortChange}
                onSelectMentor={handleSelectMentor}
                onPageChange={setPage}
              />
            </>
          )}
        </>
      )}
    </div>
  );
};

export default AnalyticsPage;
